import { IonCol, IonFab, IonFabButton, IonGrid, IonRow } from "@ionic/react";
import { differenceInSeconds, isThisYear } from "date-fns";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useHistory } from "react-router";
import { StringParam, useQueryParams, withDefault } from "use-query-params";

import CardBase from "@components/cards/CardBase";
import styles from "@components/carePlan/CarePlan.module.css";
import DocumentPopover from "@components/carePlan/DocumentPopover";
import DocumentPreview from "@components/carePlan/DocumentPreview";
import FolderPopover from "@components/carePlan/FolderPopover";
import ConsentFormSection from "@components/ConsentFormSection";
import Container from "@components/Container";
import DateStamp from "@components/dates/DateStamp";
import DirectUploadFileResourceInput from "@components/fields/DirectUploadFileResourceInput";
import FolderForm from "@components/forms/FolderForm";
import MobileHeader from "@components/MobileHeader";
import Modal from "@components/Modal";
import Icon from "@components/text/Icon";
import IconBase from "@components/text/IconBase";
import BodyCopy from "@components/typography/BodyCopy";
import ButtonLink from "@components/typography/ButtonLink";
import Heading1 from "@components/typography/Heading1";
import Heading2 from "@components/typography/Heading2";
import InternalLink from "@components/typography/InternalLink";
import useMyClient from "@hooks/useMyClient";
import useOpener from "@hooks/useOpener";
import { FileResourceFragment, FolderKindEnum, GetJourneyWithPlanQuery } from "@typing/Generated";
import { Direction } from "@typing/Models";
import { slideHorizontalAnimation } from "@utils/animationUtils";
import { getFileImage } from "@utils/fileUtils";
import { imageUrl } from "@utils/imageUtils";
import { getFileResourceTitle, getFolderTitle, sortByTimestamp } from "@utils/modelUtils";
import { isMobile } from "@utils/platformUtils";
import { journeyRoute } from "@utils/routeUtils";
import { truncate } from "@utils/stringUtils";

type Journey = GetJourneyWithPlanQuery["journey"];

type FileResourceType = NonNullable<Journey["fileResources"]>[0];

type FileResourceProps = {
  fileResource: FileResourceType;
  journey: Journey;
  setPreviewingFileResource: (fr: FileResourceFragment) => void;
};

const FileResource = ({ fileResource, journey, setPreviewingFileResource }: FileResourceProps) => {
  const intl = useIntl();

  const fileResourceDate = (file: FileResourceType) =>
    intl.formatMessage(
      {
        id:
          differenceInSeconds(file.updatedAt, file.createdAt) < 5
            ? "components.CarePlan.sections.drive.uploadedAt"
            : "components.CarePlan.sections.drive.modifiedAt"
      },
      { date: <DateStamp showWeekday={false} showYear={!isThisYear(file.updatedAt)} value={file.updatedAt} /> }
    );

  return (
    <CardBase
      body={
        <div className={styles.fileResource} key={fileResource.id}>
          <IonGrid className="ion-no-padding">
            <IonRow className="ion-align-items-center">
              <IonCol size="auto">
                <div className="ion-margin-end">
                  <img
                    aria-label={intl.formatMessage({ id: "accessibility.ariaLabels.file" })}
                    className={styles.fileResourceImage}
                    src={imageUrl(getFileImage(fileResource))}
                  />
                </div>
              </IonCol>
              <IonCol>
                <Heading2 bold color="black">
                  {getFileResourceTitle(fileResource, intl)}
                </Heading2>
              </IonCol>
              <IonCol size="auto">
                <div className={styles.dateWidth}>
                  <BodyCopy bold color="gray" size="small">
                    {fileResourceDate(fileResource)}
                  </BodyCopy>
                </div>
              </IonCol>
              <IonCol size="auto">
                <DocumentPopover fileResource={fileResource} journey={journey} />
              </IonCol>
            </IonRow>
          </IonGrid>
        </div>
      }
      bodyPadding={false}
      border={false}
      onClick={() => setPreviewingFileResource(fileResource)}
    />
  );
};

type Props = {
  journey: Journey;
};

type Folder = Journey["folders"][0];

const Drive = ({ journey }: Props) => {
  const intl = useIntl();
  const myClient = useMyClient();
  const history = useHistory();

  const [query, setQuery] = useQueryParams({
    folderId: withDefault(
      { ...StringParam, urlName: "driveFolderId" },
      isMobile() ? undefined : journey.folders.find(folder => folder.kind === FolderKindEnum.DOCUMENTS)?.id
    )
  });

  const [selectedFolderId, setSelectedFolderId] = useState<string | undefined>(query.folderId);

  const selectedFolder = journey.folders.find(folder => folder.id === selectedFolderId);

  useEffect(() => {
    if (query.folderId !== selectedFolderId) {
      setSelectedFolderId(query.folderId);
    }
  }, [query.folderId, selectedFolderId]);

  const changeFolder = (folderId: string | undefined) => {
    setQuery({ folderId });
    setSelectedFolderId(folderId);
  };

  const [previewingFileResource, setPreviewingFileResource] = useState<FileResourceFragment | null>(null);

  const folderOpener = useOpener();
  const actionsOpener = useOpener();
  const fileResourcePreviewOpener = useOpener();

  const handlePreviewFileResource = (fileResource: FileResourceFragment) => {
    setPreviewingFileResource(fileResource);
    fileResourcePreviewOpener.open();
  };

  const fileResources = sortByTimestamp(
    journey.fileResources.filter(file => !selectedFolder || file.folder?.id === selectedFolder.id),
    file => file.updatedAt
  );

  // kind is DOCUMENTS first, then CONSENT_FORM,
  const kindOrder = [FolderKindEnum.DOCUMENTS, FolderKindEnum.CONSENT_FORMS, FolderKindEnum.WORKSHEETS];

  const sortFolders = (folders: Folder[]): Folder[] => {
    // Create a shallow copy of the array
    const foldersCopy = [...folders];

    return foldersCopy.sort((a, b) => {
      // Handle null values by assigning them a high index
      const kindIndexA = a.kind === null ? kindOrder.length : kindOrder.indexOf(a.kind);
      const kindIndexB = b.kind === null ? kindOrder.length : kindOrder.indexOf(b.kind);

      if (kindIndexA !== kindIndexB) {
        return kindIndexA - kindIndexB;
      } else {
        const dateA = new Date(a.updatedAt);
        const dateB = new Date(b.updatedAt);
        return dateA.getTime() - dateB.getTime();
      }
    });
  };

  const [swipeDirection, setSwipeDirection] = useState<Direction>("right");

  const animationVariants = slideHorizontalAnimation(swipeDirection);

  const queryParams = new URLSearchParams(location.search);

  const handleCurrentFolderChange = (folderId: string | undefined, direction: Direction) => {
    setSwipeDirection(direction);
    setTimeout(() => changeFolder(folderId), 100);
  };

  const [viewportHeight, setViewportHeight] = useState(window.innerHeight);

  useEffect(() => {
    const handleResize = () => {
      setViewportHeight(window.innerHeight);
    };

    window.addEventListener("resize", handleResize);

    // Cleanup function to remove the event listener
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div>
      {selectedFolder && (
        <div className="ion-hide-lg-up">
          <MobileHeader
            handleBack={() => {
              queryParams.delete("hideHeader");
              history.replace({
                hash: location.hash,
                pathname: location.pathname,
                search: queryParams.toString()
              });
              handleCurrentFolderChange(undefined, "left");
            }}
            title={getFolderTitle(selectedFolder, intl)}
          />
        </div>
      )}
      <div
        className={`ion-hide-lg-up ${styles.mobileSpacing} ${styles.mobileScrollOverflow}`}
        style={{
          height: isMobile() ? `calc(${viewportHeight}px - 66px)` : undefined
        }}
      >
        <div className={styles.relativeContainer}>
          <AnimatePresence>
            {(() => {
              if (selectedFolder) {
                return (
                  <motion.div
                    animate="animate"
                    exit="exit"
                    initial="initial"
                    key="documents"
                    style={{ position: "absolute", width: "100%" }}
                    variants={animationVariants}
                  >
                    <div className={`flex flex-column gap-sm ${styles.folderContent}`}>
                      {selectedFolder?.kind === FolderKindEnum.CONSENT_FORMS ? (
                        <ConsentFormSection journeyId={journey.id} />
                      ) : selectedFolder?.kind === FolderKindEnum.WORKSHEETS ? (
                        journey.worksheets.map(worksheet => (
                          <div className={styles.fileResource} key={worksheet.id}>
                            <IonGrid className="ion-no-padding">
                              <IonRow className="ion-align-items-center">
                                <IonCol size="auto">
                                  <div className="ion-margin-end">
                                    <img
                                      aria-label={intl.formatMessage({ id: "accessibility.ariaLabels.file" })}
                                      className={styles.fileResourceImage}
                                      src={imageUrl("fallback")}
                                    />
                                  </div>
                                </IonCol>
                                <IonCol>
                                  <InternalLink
                                    linkEventCreateVariables={{ journeyId: journey.id }}
                                    to={journeyRoute({
                                      journeyId: journey.id,
                                      linkAfterJourney: `/worksheets/${worksheet.id}`,
                                      myClient
                                    })}
                                  >
                                    {worksheet.title}
                                  </InternalLink>
                                </IonCol>
                                <IonCol size="auto">
                                  <BodyCopy bold color="gray" size="small">
                                    <DateStamp
                                      showWeekday={false}
                                      showYear={!isThisYear(worksheet.updatedAt)}
                                      value={worksheet.updatedAt}
                                    />
                                  </BodyCopy>
                                </IonCol>
                              </IonRow>
                            </IonGrid>
                          </div>
                        ))
                      ) : null}

                      {fileResources.map(fr => (
                        <FileResource
                          fileResource={fr}
                          journey={journey}
                          key={fr.id}
                          setPreviewingFileResource={handlePreviewFileResource}
                        />
                      ))}
                    </div>
                  </motion.div>
                );
              }

              return (
                <motion.div
                  animate="animate"
                  exit="exit"
                  initial={false}
                  key="folders"
                  style={{ position: "absolute", width: "100%" }}
                  variants={animationVariants}
                >
                  <div className="margin-20-bottom margin-20-top">
                    <Heading1>
                      <FormattedMessage id="components.CarePlan.sections.drive.folders" />
                    </Heading1>
                  </div>

                  <div className={styles.mobileFolders}>
                    {sortFolders(journey.folders).map((folder, index) => (
                      <div
                        className={`${styles.folder} ${index === 0 ? styles.purpleFolder : ""}`}
                        key={folder.id}
                        onClick={() => {
                          queryParams.set("hideHeader", "true");
                          history.replace({
                            hash: location.hash,
                            pathname: location.pathname,
                            search: queryParams.toString()
                          });
                          handleCurrentFolderChange(folder.id, "right");
                        }}
                      >
                        <div className={styles.folderIcon}>
                          <Icon color="white" icon="folderOpen" />
                        </div>

                        <div className={styles.folderBottomOverlay}>
                          <BodyCopy bold color="gray-500">
                            {truncate(getFolderTitle(folder, intl), 10)}
                          </BodyCopy>
                          <FolderPopover folder={folder} journey={journey} />
                        </div>
                      </div>
                    ))}
                    <div className={`${styles.addFolder} ion-hide-lg-down`}>
                      <ButtonLink onClick={folderOpener.toggle} stylized={false}>
                        <div>
                          <div className={`${styles.addIconContainer} ion-margin-end margin-4-left`}>
                            <IconBase className={styles.addIcon} color="gray-300" icon="plus" slot="start" />
                          </div>
                          <div className="margin-10-top margin-10-right">
                            <BodyCopy color="gray-500">
                              <FormattedMessage id="components.CarePlan.sections.drive.folders" />
                            </BodyCopy>
                          </div>
                        </div>
                      </ButtonLink>
                    </div>
                  </div>
                </motion.div>
              );
            })()}
          </AnimatePresence>
        </div>
      </div>
      <div className="ion-hide-lg-down">
        <div className="ion-padding-start ion-padding-end ion-hide-lg-down">
          <Container>
            <div className="flex flex-wrap gap-lg margin-20-bottom">
              {sortFolders(journey.folders).map(folder => (
                <CardBase
                  body={
                    <div className="flex items-center gap-md">
                      <Icon color="gray-300" icon="folder" />
                      <Heading2 color="gray-500">{getFolderTitle(folder, intl)}</Heading2>
                      <FolderPopover folder={folder} icon="ellipsis" journey={journey} />
                    </div>
                  }
                  className="flex-initial"
                  key={folder.id}
                  onClick={() => {
                    changeFolder(folder.id);
                  }}
                  selected={selectedFolder?.id === folder.id}
                />
              ))}
              <ButtonLink onClick={folderOpener.toggle} stylized={false}>
                <div className="flex items-center">
                  <div className={`${styles.addIconContainer} ion-margin-end`}>
                    <IconBase className={styles.addIcon} color="gray-300" icon="plus" slot="start" />
                  </div>
                </div>
              </ButtonLink>
            </div>
            <div className="flex flex-column gap-sm">
              {selectedFolder?.kind === FolderKindEnum.CONSENT_FORMS ? (
                <ConsentFormSection journeyId={journey.id} />
              ) : selectedFolder?.kind === FolderKindEnum.WORKSHEETS ? (
                journey.worksheets.map(worksheet => (
                  <div className={styles.fileResource} key={worksheet.id}>
                    <IonGrid className="ion-no-padding">
                      <IonRow className="ion-align-items-center">
                        <IonCol size="auto">
                          <div className="ion-margin-end">
                            <img
                              aria-label={intl.formatMessage({ id: "accessibility.ariaLabels.file" })}
                              className={styles.fileResourceImage}
                              src={imageUrl("fallback")}
                            />
                          </div>
                        </IonCol>
                        <IonCol>
                          <InternalLink
                            linkEventCreateVariables={{ journeyId: journey.id }}
                            to={journeyRoute({
                              journeyId: journey.id,
                              linkAfterJourney: `/worksheets/${worksheet.id}`,
                              myClient
                            })}
                          >
                            {worksheet.title}
                          </InternalLink>
                        </IonCol>
                        <IonCol size="auto">
                          <BodyCopy bold color="gray" size="small">
                            <DateStamp
                              showWeekday={false}
                              showYear={!isThisYear(worksheet.updatedAt)}
                              value={worksheet.updatedAt}
                            />
                          </BodyCopy>
                        </IonCol>
                      </IonRow>
                    </IonGrid>
                  </div>
                ))
              ) : null}

              {fileResources.map(fr => (
                <FileResource
                  fileResource={fr}
                  journey={journey}
                  key={fr.id}
                  setPreviewingFileResource={handlePreviewFileResource}
                />
              ))}
            </div>
          </Container>
          <div className="margin-20-top">
            <DirectUploadFileResourceInput folderId={selectedFolder?.id} journeyId={journey.id} />
          </div>
          <Modal
            opener={folderOpener}
            title={intl.formatMessage({
              id: "pages.documents.createFolder"
            })}
          >
            {({ modalDisplayed }) => (
              <FolderForm
                autofocus={modalDisplayed}
                journey={journey}
                onCancel={folderOpener.close}
                onSuccess={folderOpener.close}
              />
            )}
          </Modal>
          <Modal opener={actionsOpener}>
            {() => (
              <>
                <ButtonLink
                  className={`flex ${styles.careTeamDetail}`}
                  onClick={() => {
                    actionsOpener.close();
                    folderOpener.open();
                  }}
                  stylized={false}
                >
                  <Icon className="ion-margin-end margin-2-top" color="gray-200" icon="folderOpen" />
                  <BodyCopy>
                    <FormattedMessage id="pages.documents.createFolder" />
                  </BodyCopy>
                </ButtonLink>
                <DirectUploadFileResourceInput
                  className="ion-hide-lg-up"
                  folderId={selectedFolder?.id}
                  journeyId={journey.id}
                  onSuccess={fr => {
                    actionsOpener.close();
                    queryParams.set("hideHeader", "true");
                    history.replace({
                      hash: location.hash,
                      pathname: location.pathname,
                      search: queryParams.toString()
                    });
                    handleCurrentFolderChange(fr.folder?.id, "right");
                  }}
                  overrideContent={
                    <div className={`flex ${styles.careTeamDetail}`}>
                      <Icon className="ion-margin-end margin-2-top" color="gray-200" icon="fileDownload" />
                      <BodyCopy>
                        <FormattedMessage id="pages.documents.uploadFile" />
                      </BodyCopy>
                    </div>
                  }
                />
              </>
            )}
          </Modal>
        </div>
      </div>

      <DirectUploadFileResourceInput
        className="ion-hide-lg-up"
        folderId={selectedFolder?.id}
        journeyId={journey.id}
        overrideContent={
          <IonFab
            className="ion-hide-lg-up"
            horizontal="end"
            slot="fixed"
            style={{ bottom: "56px", right: "56px" }}
            vertical="bottom"
          >
            <IonFabButton
              aria-label={intl.formatMessage({ id: "accessibility.ariaLabels.toggleTags" })}
              color="primary-500"
              onClick={e => {
                if (!selectedFolder) {
                  e.preventDefault();
                  e.stopPropagation();
                  actionsOpener.open();
                }
              }}
            >
              <Icon color="white" icon="plus" />
            </IonFabButton>
          </IonFab>
        }
      />

      <DocumentPreview fileResource={previewingFileResource} journey={journey} opener={fileResourcePreviewOpener} />
    </div>
  );
};

export default Drive;
